import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  DollarSign, Users, Truck, Star, Check, X, AlertTriangle,
  Loader2, Plus, Tag, RefreshCw, TrendingUp, MessageSquare,
  Calendar, Clock, Eye, ThumbsUp, ThumbsDown, Megaphone, Image,
  ExternalLink, Trash2, Edit, ToggleLeft, ToggleRight, Zap, Crown
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { StatsGrid } from '../dashboard/components';

export default function RoadsideAdminPage() {
  const { api, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Reviews
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('pending');
  const [processingReview, setProcessingReview] = useState(null);
  
  // Promo Codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: '',
    discount_type: 'fixed',
    discount_value: 0,
    max_uses: '',
    expires_at: '',
    description: ''
  });
  
  // Advertisements
  const [ads, setAds] = useState([]);
  const [showAdDialog, setShowAdDialog] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    placement: 'banner', // banner, sidebar, popup
    target_audience: 'all', // all, customers, providers
    priority: 1,
    start_date: '',
    end_date: ''
  });
  
  // Surge Pricing Settings
  const [surgeSettings, setSurgeSettings] = useState({
    enabled: true,
    multiplier: 1.5,
    peak_hours: [[0, 5], [22, 24]],
    low_availability_threshold: 2
  });
  const [savingSurge, setSavingSurge] = useState(false);
  
  // Platform Pricing Settings
  const [pricingSettings, setPricingSettings] = useState({
    customer_platform_fee: 5.00,
    provider_platform_fee: 5.00,
    surge_fee: 10.00,
    premium_monthly_fee: 49.99,
    premium_reduced_fee: 2.50
  });
  const [savingPricing, setSavingPricing] = useState(false);
  
  // All Subscriptions (marketplace & providers)
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchReviews();
    fetchPromoCodes();
    fetchAds();
    fetchSurgeSettings();
    fetchPricingSettings();
    fetchAllSubscriptions();
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [reviewFilter]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/roadside/admin/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
    setLoading(false);
  };

  const fetchReviews = async () => {
    try {
      const res = await api.get('/roadside/admin/reviews', {
        params: { status: reviewFilter }
      });
      setReviews(res.data || []);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const res = await api.get('/roadside/admin/promo-codes');
      setPromoCodes(res.data || []);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    }
  };

  const handleModerateReview = async (reviewId, action) => {
    setProcessingReview(reviewId);
    try {
      await api.put(`/roadside/admin/reviews/${reviewId}/moderate`, null, {
        params: { action }
      });
      toast.success(`Review ${action}d`);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to moderate review');
    }
    setProcessingReview(null);
  };

  const handleCreatePromoCode = async () => {
    try {
      const data = {
        ...promoForm,
        discount_value: parseFloat(promoForm.discount_value),
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null,
        expires_at: promoForm.expires_at || null
      };
      await api.post('/roadside/admin/promo-codes', data);
      toast.success('Promo code created');
      setShowPromoDialog(false);
      setPromoForm({
        code: '',
        discount_type: 'fixed',
        discount_value: 0,
        max_uses: '',
        expires_at: '',
        description: ''
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleTogglePromoCode = async (promoId, isActive) => {
    try {
      await api.put(`/roadside/admin/promo-codes/${promoId}`, null, {
        params: { is_active: !isActive }
      });
      toast.success(`Promo code ${!isActive ? 'activated' : 'deactivated'}`);
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to update promo code');
    }
  };

  // Advertisements functions
  const fetchAds = async () => {
    try {
      const res = await api.get('/roadside/admin/ads');
      setAds(res.data || []);
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    }
  };

  const fetchSurgeSettings = async () => {
    try {
      const res = await api.get('/roadside/admin/settings');
      if (res.data?.surge_pricing) {
        setSurgeSettings(res.data.surge_pricing);
      }
    } catch (error) {
      console.error('Failed to fetch surge settings:', error);
    }
  };

  const handleSaveSurgeSettings = async () => {
    setSavingSurge(true);
    try {
      await api.put('/roadside/admin/settings/surge', surgeSettings);
      toast.success('Surge pricing settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
    setSavingSurge(false);
  };

  const fetchPricingSettings = async () => {
    try {
      const res = await api.get('/roadside/admin/settings');
      if (res.data?.pricing) {
        setPricingSettings(res.data.pricing);
      }
    } catch (error) {
      console.error('Failed to fetch pricing settings:', error);
    }
  };

  const handleSavePricingSettings = async () => {
    setSavingPricing(true);
    try {
      await api.put('/roadside/admin/settings/pricing', pricingSettings);
      toast.success('Pricing settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing settings');
    }
    setSavingPricing(false);
  };

  const fetchAllSubscriptions = async () => {
    setLoadingSubscriptions(true);
    try {
      const res = await api.get('/roadside/admin/subscriptions');
      setSubscriptions(res.data || []);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    }
    setLoadingSubscriptions(false);
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    try {
      await api.post(`/roadside/admin/subscriptions/${subscriptionId}/cancel`);
      toast.success('Subscription cancelled');
      fetchAllSubscriptions();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleCreateAd = async () => {
    try {
      const data = {
        ...adForm,
        priority: parseInt(adForm.priority) || 1
      };
      
      if (editingAd) {
        await api.put(`/roadside/admin/ads/${editingAd.id}`, data);
        toast.success('Advertisement updated');
      } else {
        await api.post('/roadside/admin/ads', data);
        toast.success('Advertisement created');
      }
      
      setShowAdDialog(false);
      setEditingAd(null);
      setAdForm({
        title: '',
        description: '',
        image_url: '',
        link_url: '',
        placement: 'banner',
        target_audience: 'all',
        priority: 1,
        start_date: '',
        end_date: ''
      });
      fetchAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save advertisement');
    }
  };

  const handleToggleAd = async (adId, isActive) => {
    try {
      await api.put(`/roadside/admin/ads/${adId}/toggle`, null, {
        params: { is_active: !isActive }
      });
      toast.success(`Advertisement ${!isActive ? 'activated' : 'deactivated'}`);
      fetchAds();
    } catch (error) {
      toast.error('Failed to toggle advertisement');
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;
    
    try {
      await api.delete(`/roadside/admin/ads/${adId}`);
      toast.success('Advertisement deleted');
      fetchAds();
    } catch (error) {
      toast.error('Failed to delete advertisement');
    }
  };

  const openEditAd = (ad) => {
    setEditingAd(ad);
    setAdForm({
      title: ad.title || '',
      description: ad.description || '',
      image_url: ad.image_url || '',
      link_url: ad.link_url || '',
      placement: ad.placement || 'banner',
      target_audience: ad.target_audience || 'all',
      priority: ad.priority || 1,
      start_date: ad.start_date || '',
      end_date: ad.end_date || ''
    });
    setShowAdDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="roadside-admin-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Roadside Rescue Admin</h1>
          <p className="text-slate-400 text-sm">Manage the roadside assistance marketplace</p>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="reviews">Review Moderation</TabsTrigger>
            <TabsTrigger value="promos">Promo Codes</TabsTrigger>
            <TabsTrigger value="ads">Advertisements</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            {stats && (
              <StatsGrid
                stats={[
                  {
                    id: 'total-requests',
                    title: 'Total Requests',
                    value: stats.requests?.total || 0,
                    icon: Truck,
                    iconBgColor: 'bg-blue-500/20',
                    iconColor: 'text-blue-400',
                    testId: 'stat-total-requests'
                  },
                  {
                    id: 'pending',
                    title: 'Pending',
                    value: stats.requests?.pending || 0,
                    icon: AlertTriangle,
                    iconBgColor: 'bg-amber-500/20',
                    iconColor: 'text-amber-400',
                    testId: 'stat-pending'
                  },
                  {
                    id: 'completed',
                    title: 'Completed',
                    value: stats.requests?.completed || 0,
                    icon: Check,
                    iconBgColor: 'bg-green-500/20',
                    iconColor: 'text-green-400',
                    testId: 'stat-completed'
                  },
                  {
                    id: 'providers',
                    title: 'Providers',
                    value: stats.providers?.total || 0,
                    icon: Users,
                    iconBgColor: 'bg-purple-500/20',
                    iconColor: 'text-purple-400',
                    testId: 'stat-providers'
                  }
                ]}
                columns={4}
                className="mb-6"
              />
            )}
            
            {/* Revenue */}
            {stats?.revenue && (
              <StatsGrid
                stats={[
                  {
                    id: 'customer-fees',
                    title: 'Customer Fees',
                    value: stats.revenue.customer_fees?.toFixed(2) || '0.00',
                    prefix: '$',
                    icon: DollarSign,
                    iconBgColor: 'bg-green-500/20',
                    iconColor: 'text-green-400',
                    valueColor: 'text-green-400',
                    description: 'Total platform fees from customers',
                    testId: 'stat-customer-fees'
                  },
                  {
                    id: 'provider-fees',
                    title: 'Provider Fees',
                    value: stats.revenue.provider_fees?.toFixed(2) || '0.00',
                    prefix: '$',
                    icon: DollarSign,
                    iconBgColor: 'bg-blue-500/20',
                    iconColor: 'text-blue-400',
                    valueColor: 'text-blue-400',
                    description: 'Total platform fees from providers',
                    testId: 'stat-provider-fees'
                  }
                ]}
                columns={2}
              />
            )}
          </TabsContent>
          
          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Select value={reviewFilter} onValueChange={setReviewFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending Review</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-400">
                      {reviews.length} review{reviews.length !== 1 ? 's' : ''} found
                    </p>
                  </div>
                  
                  <Button variant="outline" size="sm" onClick={fetchReviews}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {reviews.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Star className="h-12 w-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400 font-medium">No {reviewFilter} reviews</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {reviewFilter === 'pending' 
                      ? 'All reviews have been moderated' 
                      : `No reviews with ${reviewFilter} status`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card key={review.id} data-testid={`review-${review.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Header with reviewer info and rating */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-lg font-semibold">
                              {review.reviewer_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="font-medium">{review.reviewer_name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <div className="flex items-center">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`h-4 w-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-700'}`}
                                    />
                                  ))}
                                </div>
                                <span className="text-sm text-slate-400">
                                  {review.rating}/5
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Review details */}
                          <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                            <div className="flex items-center gap-2 text-slate-400">
                              <Truck className="h-4 w-4" />
                              <span>Service: <span className="text-slate-300 capitalize">{review.service_type?.replace('_', ' ')}</span></span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-400">
                              <Calendar className="h-4 w-4" />
                              <span>{new Date(review.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          
                          {/* Comment */}
                          {review.comment ? (
                            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                              <div className="flex items-start gap-2">
                                <MessageSquare className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                                <p className="text-sm text-slate-300 italic">"{review.comment}"</p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 italic">No comment provided</p>
                          )}
                        </div>
                        
                        {/* Action buttons */}
                        {reviewFilter === 'pending' && (
                          <div className="flex flex-col gap-2 shrink-0">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-500"
                              onClick={() => handleModerateReview(review.id, 'approve')}
                              disabled={processingReview === review.id}
                              data-testid={`approve-review-${review.id}`}
                            >
                              {processingReview === review.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <ThumbsUp className="h-4 w-4 mr-1" />
                                  Approve
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                              onClick={() => handleModerateReview(review.id, 'reject')}
                              disabled={processingReview === review.id}
                              data-testid={`reject-review-${review.id}`}
                            >
                              <ThumbsDown className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                        
                        {/* Status badge for non-pending */}
                        {reviewFilter !== 'pending' && (
                          <Badge className={reviewFilter === 'approved' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30'
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }>
                            {reviewFilter === 'approved' ? (
                              <><Check className="h-3 w-3 mr-1" /> Approved</>
                            ) : (
                              <><X className="h-3 w-3 mr-1" /> Rejected</>
                            )}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Promo Codes Tab */}
          <TabsContent value="promos">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Promo Codes</h3>
              <Button onClick={() => setShowPromoDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Code
              </Button>
            </div>
            
            {promoCodes.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center">
                  <Tag className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-400">No promo codes yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {promoCodes.map((promo) => (
                  <Card key={promo.id} className={!promo.is_active ? 'opacity-50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <code className="bg-slate-800 px-2 py-0.5 rounded font-mono text-blue-400">
                              {promo.code}
                            </code>
                            <Badge variant={promo.is_active ? 'default' : 'secondary'}>
                              {promo.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400">
                            {promo.discount_type === 'percent' 
                              ? `${promo.discount_value}% off`
                              : `$${promo.discount_value.toFixed(2)} off`
                            }
                            {promo.max_uses && ` • ${promo.use_count || 0}/${promo.max_uses} uses`}
                          </p>
                          {promo.description && (
                            <p className="text-xs text-slate-500 mt-1">{promo.description}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTogglePromoCode(promo.id, promo.is_active)}
                        >
                          {promo.is_active ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Advertisements Tab */}
          <TabsContent value="ads">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">Advertisements</h3>
                <p className="text-sm text-slate-400">Manage promotional banners and ads</p>
              </div>
              <Button onClick={() => {
                setEditingAd(null);
                setAdForm({
                  title: '',
                  description: '',
                  image_url: '',
                  link_url: '',
                  placement: 'banner',
                  target_audience: 'all',
                  priority: 1,
                  start_date: '',
                  end_date: ''
                });
                setShowAdDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Ad
              </Button>
            </div>
            
            {ads.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Megaphone className="h-12 w-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400 font-medium">No advertisements yet</p>
                  <p className="text-sm text-slate-500 mt-1">Create your first ad to promote services</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {ads.map((ad) => (
                  <Card key={ad.id} className={!ad.is_active ? 'opacity-60' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Ad Preview */}
                        {ad.image_url ? (
                          <div className="w-32 h-20 rounded-lg overflow-hidden bg-slate-800 shrink-0">
                            <img 
                              src={ad.image_url} 
                              alt={ad.title} 
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        ) : (
                          <div className="w-32 h-20 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                            <Image className="h-8 w-8 text-slate-600" />
                          </div>
                        )}
                        
                        {/* Ad Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium truncate">{ad.title}</h4>
                            <Badge className={ad.is_active ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                              {ad.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {ad.placement === 'banner' ? '🖼️ Banner' : ad.placement === 'sidebar' ? '📋 Sidebar' : '💬 Popup'}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-slate-400 line-clamp-1 mb-2">{ad.description || 'No description'}</p>
                          
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>📍 {ad.target_audience === 'all' ? 'Everyone' : ad.target_audience === 'customers' ? 'Customers' : 'Providers'}</span>
                            <span>⭐ Priority: {ad.priority}</span>
                            {ad.clicks !== undefined && <span>👆 {ad.clicks || 0} clicks</span>}
                            {ad.impressions !== undefined && <span>👁️ {ad.impressions || 0} views</span>}
                          </div>
                          
                          {ad.link_url && (
                            <a 
                              href={ad.link_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-2"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {ad.link_url.substring(0, 40)}...
                            </a>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleAd(ad.id, ad.is_active)}
                            title={ad.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {ad.is_active ? <ToggleRight className="h-4 w-4 text-green-400" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditAd(ad)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAd(ad.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Settings Tab - Pricing, Subscriptions, Surge */}
          <TabsContent value="settings" data-testid="settings-tab">
            {/* Platform Pricing Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-400" />
                  Platform Pricing
                </CardTitle>
                <p className="text-sm text-slate-400">
                  Manage platform fees for customers and providers
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Platform Fee</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">$</span>
                      <Input
                        type="number"
                        step="0.50"
                        min="0"
                        value={pricingSettings.customer_platform_fee}
                        onChange={(e) => setPricingSettings({...pricingSettings, customer_platform_fee: parseFloat(e.target.value) || 5.00})}
                        className="w-24"
                        data-testid="customer-fee-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Fee charged to customers per request</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Provider Platform Fee</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">$</span>
                      <Input
                        type="number"
                        step="0.50"
                        min="0"
                        value={pricingSettings.provider_platform_fee}
                        onChange={(e) => setPricingSettings({...pricingSettings, provider_platform_fee: parseFloat(e.target.value) || 5.00})}
                        className="w-24"
                        data-testid="provider-fee-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Fee charged to providers per job</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Surge Additional Fee</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">$</span>
                      <Input
                        type="number"
                        step="1.00"
                        min="0"
                        value={pricingSettings.surge_fee}
                        onChange={(e) => setPricingSettings({...pricingSettings, surge_fee: parseFloat(e.target.value) || 10.00})}
                        className="w-24"
                        data-testid="surge-fee-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Additional fee during surge periods</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700">
                  <div className="space-y-2">
                    <Label>Premium Monthly Fee</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">$</span>
                      <Input
                        type="number"
                        step="5.00"
                        min="0"
                        value={pricingSettings.premium_monthly_fee}
                        onChange={(e) => setPricingSettings({...pricingSettings, premium_monthly_fee: parseFloat(e.target.value) || 49.99})}
                        className="w-24"
                        data-testid="premium-fee-input"
                      />
                      <span className="text-slate-400">/month</span>
                    </div>
                    <p className="text-xs text-slate-500">Premium subscription monthly price</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Premium Reduced Fee</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">$</span>
                      <Input
                        type="number"
                        step="0.50"
                        min="0"
                        value={pricingSettings.premium_reduced_fee}
                        onChange={(e) => setPricingSettings({...pricingSettings, premium_reduced_fee: parseFloat(e.target.value) || 2.50})}
                        className="w-24"
                        data-testid="premium-reduced-fee-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Per-job fee for Premium providers</p>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={handleSavePricingSettings} 
                    disabled={savingPricing}
                    data-testid="save-pricing-btn"
                  >
                    {savingPricing ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                    ) : (
                      <><Check className="h-4 w-4 mr-2" /> Save Pricing</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Subscriptions Management Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-400" />
                  All Subscriptions
                </CardTitle>
                <p className="text-sm text-slate-400">
                  Manage all premium subscriptions across the marketplace
                </p>
              </CardHeader>
              <CardContent>
                {loadingSubscriptions ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Crown className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No active subscriptions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {subscriptions.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${sub.type === 'premium_provider' ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                            <Crown className={`h-5 w-5 ${sub.type === 'premium_provider' ? 'text-amber-400' : 'text-blue-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{sub.business_name || sub.user_name}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-400">
                              <span>{sub.type === 'premium_provider' ? 'Provider Premium' : 'Marketplace Premium'}</span>
                              <span>•</span>
                              <span>${sub.monthly_fee}/mo</span>
                              {sub.subscribed_at && (
                                <>
                                  <span>•</span>
                                  <span>Since {new Date(sub.subscribed_at).toLocaleDateString()}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={sub.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {sub.status}
                          </Badge>
                          {sub.status === 'active' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleCancelSubscription(sub.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Surge Pricing Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-400" />
                  Surge Pricing Configuration
                </CardTitle>
                <p className="text-sm text-slate-400">
                  Manage dynamic pricing based on demand and availability
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div>
                    <p className="font-medium">Enable Surge Pricing</p>
                    <p className="text-sm text-slate-400">
                      Automatically increase prices during high demand periods
                    </p>
                  </div>
                  <Button
                    variant={surgeSettings.enabled ? "default" : "outline"}
                    onClick={() => setSurgeSettings({...surgeSettings, enabled: !surgeSettings.enabled})}
                    data-testid="surge-toggle-btn"
                  >
                    {surgeSettings.enabled ? (
                      <><ToggleRight className="h-4 w-4 mr-2 text-green-400" /> Enabled</>
                    ) : (
                      <><ToggleLeft className="h-4 w-4 mr-2" /> Disabled</>
                    )}
                  </Button>
                </div>
                
                {surgeSettings.enabled && (
                  <>
                    {/* Multiplier Setting */}
                    <div className="space-y-2">
                      <Label>Price Multiplier</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          type="number"
                          step="0.1"
                          min="1"
                          max="3"
                          value={surgeSettings.multiplier}
                          onChange={(e) => setSurgeSettings({...surgeSettings, multiplier: parseFloat(e.target.value) || 1.5})}
                          className="w-32"
                          data-testid="surge-multiplier-input"
                        />
                        <span className="text-sm text-slate-400">
                          x base price (e.g., ${pricingSettings.customer_platform_fee.toFixed(2)} → ${(pricingSettings.customer_platform_fee * surgeSettings.multiplier).toFixed(2)})
                        </span>
                      </div>
                    </div>
                    
                    {/* Low Availability Threshold */}
                    <div className="space-y-2">
                      <Label>Low Availability Threshold</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={surgeSettings.low_availability_threshold}
                          onChange={(e) => setSurgeSettings({...surgeSettings, low_availability_threshold: parseInt(e.target.value) || 2})}
                          className="w-32"
                          data-testid="surge-threshold-input"
                        />
                        <span className="text-sm text-slate-400">
                          Activate surge when fewer than this many providers are available
                        </span>
                      </div>
                    </div>
                    
                    {/* Peak Hours */}
                    <div className="space-y-2">
                      <Label>Peak Hours (24h format)</Label>
                      <p className="text-sm text-slate-400 mb-3">
                        Surge pricing is automatically active during these hours
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                          <p className="text-xs text-slate-400 mb-1">Late Night</p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="23"
                              value={surgeSettings.peak_hours?.[0]?.[0] || 0}
                              onChange={(e) => {
                                const newPeakHours = [...(surgeSettings.peak_hours || [[0,5], [22,24]])];
                                newPeakHours[0] = [parseInt(e.target.value) || 0, newPeakHours[0][1]];
                                setSurgeSettings({...surgeSettings, peak_hours: newPeakHours});
                              }}
                              className="w-20"
                            />
                            <span className="text-slate-500">to</span>
                            <Input
                              type="number"
                              min="0"
                              max="24"
                              value={surgeSettings.peak_hours?.[0]?.[1] || 5}
                              onChange={(e) => {
                                const newPeakHours = [...(surgeSettings.peak_hours || [[0,5], [22,24]])];
                                newPeakHours[0] = [newPeakHours[0][0], parseInt(e.target.value) || 5];
                                setSurgeSettings({...surgeSettings, peak_hours: newPeakHours});
                              }}
                              className="w-20"
                            />
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                          <p className="text-xs text-slate-400 mb-1">Evening</p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="23"
                              value={surgeSettings.peak_hours?.[1]?.[0] || 22}
                              onChange={(e) => {
                                const newPeakHours = [...(surgeSettings.peak_hours || [[0,5], [22,24]])];
                                newPeakHours[1] = [parseInt(e.target.value) || 22, newPeakHours[1][1]];
                                setSurgeSettings({...surgeSettings, peak_hours: newPeakHours});
                              }}
                              className="w-20"
                            />
                            <span className="text-slate-500">to</span>
                            <Input
                              type="number"
                              min="0"
                              max="24"
                              value={surgeSettings.peak_hours?.[1]?.[1] || 24}
                              onChange={(e) => {
                                const newPeakHours = [...(surgeSettings.peak_hours || [[0,5], [22,24]])];
                                newPeakHours[1] = [newPeakHours[1][0], parseInt(e.target.value) || 24];
                                setSurgeSettings({...surgeSettings, peak_hours: newPeakHours});
                              }}
                              className="w-20"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-slate-700">
                  <Button 
                    onClick={handleSaveSurgeSettings} 
                    disabled={savingSurge}
                    data-testid="save-surge-btn"
                  >
                    {savingSurge ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                    ) : (
                      <><Check className="h-4 w-4 mr-2" /> Save Settings</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Create Promo Code Dialog */}
        <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Promo Code</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Code *</Label>
                <Input
                  value={promoForm.code}
                  onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="SAVE5"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Discount Type</Label>
                  <Select 
                    value={promoForm.discount_type} 
                    onValueChange={(v) => setPromoForm({ ...promoForm, discount_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                      <SelectItem value="percent">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Discount Value *</Label>
                  <Input
                    type="number"
                    value={promoForm.discount_value}
                    onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })}
                    placeholder={promoForm.discount_type === 'percent' ? '10' : '5'}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Max Uses (optional)</Label>
                  <Input
                    type="number"
                    value={promoForm.max_uses}
                    onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                    placeholder="100"
                  />
                </div>
                <div>
                  <Label>Expires (optional)</Label>
                  <Input
                    type="date"
                    value={promoForm.expires_at}
                    onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Description (optional)</Label>
                <Input
                  value={promoForm.description}
                  onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                  placeholder="Holiday promotion"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPromoDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreatePromoCode}
                disabled={!promoForm.code || !promoForm.discount_value}
              >
                Create Code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Create/Edit Advertisement Dialog */}
        <Dialog open={showAdDialog} onOpenChange={setShowAdDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingAd ? 'Edit Advertisement' : 'Create Advertisement'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label>Title *</Label>
                <Input
                  value={adForm.title}
                  onChange={(e) => setAdForm({ ...adForm, title: e.target.value })}
                  placeholder="Premium Provider Promotion"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={adForm.description}
                  onChange={(e) => setAdForm({ ...adForm, description: e.target.value })}
                  placeholder="Get 50% off your first month as a premium provider..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Image URL</Label>
                <Input
                  value={adForm.image_url}
                  onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })}
                  placeholder="https://example.com/banner.jpg"
                />
                {adForm.image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden bg-slate-800 h-24">
                    <img 
                      src={adForm.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              <div>
                <Label>Link URL (where ad clicks go)</Label>
                <Input
                  value={adForm.link_url}
                  onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })}
                  placeholder="https://example.com/promo"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Placement</Label>
                  <Select 
                    value={adForm.placement} 
                    onValueChange={(v) => setAdForm({ ...adForm, placement: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="banner">🖼️ Banner (top)</SelectItem>
                      <SelectItem value="sidebar">📋 Sidebar</SelectItem>
                      <SelectItem value="popup">💬 Popup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Audience</Label>
                  <Select 
                    value={adForm.target_audience} 
                    onValueChange={(v) => setAdForm({ ...adForm, target_audience: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Everyone</SelectItem>
                      <SelectItem value="customers">Customers Only</SelectItem>
                      <SelectItem value="providers">Providers Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Priority (1-10)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={adForm.priority}
                    onChange={(e) => setAdForm({ ...adForm, priority: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={adForm.start_date}
                    onChange={(e) => setAdForm({ ...adForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={adForm.end_date}
                    onChange={(e) => setAdForm({ ...adForm, end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAd}
                disabled={!adForm.title}
              >
                {editingAd ? 'Update Ad' : 'Create Ad'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
